/**
 * Trendy Workspace — the per-seat operational surface.
 *
 * Three columns: seat rail, the day's work, the copilot. Same frame on every
 * tab; only the centre swaps. Both rails are resizable and remember their
 * width.
 *
 * Phase 0 step 4.5: the frame exists across all five tabs with placeholder
 * content. Step 5 fills in resolvers without this file changing much.
 *
 * Grouped vs ranked is decided by the API's `layout`, not here — Today is the
 * only heterogeneous tab (four different kinds of response) so it groups;
 * every other tab is one kind of thing and ranks.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2 } from 'lucide-react'

import '../components/workspace/workspace.css'
import ResizableRail from '../components/workspace/ResizableRail'
import { Bucket, ComposedSlot, DraftPanel, Notes, PreviewNotice, RailChat, Row, SetupGaps } from '../components/workspace/frame'

const DEFAULT_TAB = 'today'

/** Per-source freshness. Not one "built at" stamp — the two go stale apart. */
function SyncFooter({ sources }) {
  // 'off' is amber, not grey. A source nobody connected is something to act on,
  // and greying it would file it alongside "unknown" — the one reading the page
  // then cannot tell a source that is missing from one that is merely quiet.
  const colour = (state) => (
    state === 'live' ? 'var(--ws-teal)'
      : state === 'stale' || state === 'off' ? 'var(--ws-amber)'
        : 'var(--ws-mut)'
  )
  return (
    <div className="text-[11px] leading-relaxed" style={{ color: 'var(--ws-mut)' }}>
      {(sources || []).map((s) => (
        <div key={s.key} className="flex items-center gap-2">
          <span
            className="w-1.5 h-1.5 shrink-0"
            style={{ background: colour(s.state) }}
            aria-hidden="true"
          />
          <span>
            {s.label} · {s.detail || s.state}
            {/* What the source covers. Always shown, never as an alarm — a
                constant is frame, and a warning that never changes is ignored. */}
            {s.scope && (
              <span className="block" style={{ color: 'var(--ws-mut)', opacity: 0.85 }}>
                {s.scope}
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  )
}

function SeatRail({ seat, tabs, activeTab, onSelectTab, sources }) {
  const navigate = useNavigate()

  return (
    <div className="flex flex-col h-full min-h-0 py-5 overflow-y-auto" style={{ background: 'var(--ws-panel)', borderRight: '1px solid var(--ws-line)' }}>
      <div className="px-5 pb-5">
        <p className="text-lg font-semibold tracking-tight" style={{ color: 'var(--ws-tx)' }}>Trendy</p>
        <p className="text-[11px]" style={{ color: 'var(--ws-mut)' }}>Workspace</p>
      </div>

      <div className="px-5 pb-4 mb-3" style={{ borderBottom: '1px solid var(--ws-line-2)' }}>
        <p className="text-[13px] font-semibold truncate" style={{ color: 'var(--ws-tx)' }}>
          {seat?.username}
        </p>
        <p className="text-[11px]" style={{ color: 'var(--ws-mut)' }}>{seat?.role_label}</p>
        {seat?.mailbox && (
          <p className="mt-1 text-[11px] truncate" style={{ color: 'var(--ws-mut)' }} title={seat.mailbox}>
            {seat.mailbox}
          </p>
        )}
      </div>

      <nav className="flex flex-col">
        {(tabs || []).map((t) => {
          const active = t.key === activeTab
          return (
            <button
              key={t.key}
              onClick={() => onSelectTab(t.key)}
              aria-current={active}
              className="flex justify-between items-center gap-2 px-5 py-1.5 text-[13px] text-left"
              style={{
                borderLeft: `2px solid ${active ? 'var(--ws-amber)' : 'transparent'}`,
                background: active ? 'var(--ws-panel-2)' : 'transparent',
                color: active ? 'var(--ws-tx)' : 'var(--ws-tx-2)',
                fontWeight: active ? 600 : 400,
              }}
            >
              <span>{t.title}</span>
              {/* Counts are content. Null until a resolver produces them —
                  a fabricated count would be the first lie on the page. */}
              {t.count != null && (
                <span className="ws-num text-[11px]" style={{ color: 'var(--ws-mut)' }}>{t.count}</span>
              )}
            </button>
          )
        })}
      </nav>

      <div className="mt-auto px-5 pt-4" style={{ borderTop: '1px solid var(--ws-line-2)' }}>
        <SyncFooter sources={sources} />
        <button
          onClick={() => navigate('/')}
          className="mt-3 inline-flex items-center gap-1.5 text-[11px]"
          style={{ color: 'var(--ws-mut)' }}
        >
          <ArrowLeft size={12} />
          Trendy homepage
        </button>
      </div>
    </div>
  )
}

/**
 * The copilot rail — the seam.
 *
 * Three things pass through it, and the order matters: the record you selected
 * arrives as context, a draft written from that record's own claims appears
 * below it, and the question you type carries the record with it.
 *
 * Conversation posts to the same /api/chat the main application uses. The claim
 * it had not been wired because the chat stack was entangled in ChatApp was
 * half true — the STREAMING ui is entangled, and none of it was needed to make
 * the box answer a question.
 */
function CopilotRail({ context, draft, draftFor, onDismiss, askContext }) {
  return (
    <div className="flex flex-col h-full min-h-0" style={{ background: 'var(--ws-panel)', borderLeft: '1px solid var(--ws-line)' }}>
      <div className="px-5 py-4 shrink-0" style={{ borderBottom: '1px solid var(--ws-line-2)' }}>
        <p className="text-[13px] font-semibold" style={{ color: 'var(--ws-tx)' }}>Trendy</p>
        <p className="mt-0.5 text-[11px]" style={{ color: 'var(--ws-mut)' }}>
          Asks your systems. Drafts. You send.
        </p>
      </div>

      {/* Context and draft scroll INSIDE their own capped block.

          They used to be `shrink-0` siblings of the ask box in one scrolling
          column, which flexbox resolves by taking every pixel out of the ask
          box: it is the only child allowed to shrink. A draft past about fifty
          wrapped lines — an ordinary generated email in a 330px rail — drove
          the box a thousand pixels below the fold. It was still reachable, by
          scrolling the rail down past the whole draft, which is not a thing
          anyone discovers. The report was "I can't type anything into the
          workspace chat", and that was accurate.

          Capping at 45% means a long draft scrolls within its own region
          instead of pushing the conversation off the end of the rail. */}
      <div className="shrink-0 max-h-[45%] overflow-y-auto px-5 pt-4">
        <div
          className="px-3 py-2 text-[11px]"
          style={{ border: '1px solid var(--ws-line-2)', background: 'var(--ws-panel-2)', color: 'var(--ws-mut)' }}
        >
          <b style={{ color: 'var(--ws-tx-2)' }}>Context:</b> {draftFor || context}
        </div>

        {draft && (
          <div className="mt-4">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--ws-mut)' }}>
                Draft
              </p>
              <button onClick={onDismiss} className="text-[11px]" style={{ color: 'var(--ws-mut)' }}>
                Dismiss
              </button>
            </div>
            <DraftPanel draft={draft} />
          </div>
        )}
      </div>

      {/* The box that could not ask. A direct child of the rail, so its own
          composer pins to the RAIL's bottom and not to the bottom of whatever
          happens to be above it. It posts to the same /api/chat the main
          application uses, carrying the record the question was asked from. */}
      <div className="flex-1 min-h-0 px-5 py-4">
        <RailChat context={askContext} />
      </div>
    </div>
  )
}

/**
 * The workspace below the three-column breakpoint.
 *
 * Both rails are `hidden lg:flex`, which is right for the SHAPE — three columns
 * in a 900px window would leave the day's work about 350px wide — but wrong for
 * what it did to their CONTENTS. Under 1024px the tab list and the ask box were
 * not laid out differently; they were removed, with nothing on screen saying so
 * and no way to get them back short of widening the window. The ask box in
 * particular stayed in the DOM, enabled and correctly wired, at zero by zero
 * pixels: present to every check except the only one that matters, which is
 * whether a person can click it and type.
 *
 * So the columns still collapse, but what was in them stays reachable: the tabs
 * move into a bar, and the copilot opens over the page. Hiding a layout is a
 * judgement about screen width. Hiding a capability is a bug.
 */
function NarrowBar({ tabs, activeTab, onSelectTab, onAsk }) {
  return (
    <div
      className="lg:hidden fixed bottom-0 inset-x-0 z-30 flex items-center gap-1 px-3 py-2 overflow-x-auto"
      style={{ background: 'var(--ws-panel)', borderTop: '1px solid var(--ws-line)' }}
    >
      <nav className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto">
        {(tabs || []).map((t) => {
          const active = t.key === activeTab
          return (
            <button
              key={t.key}
              onClick={() => onSelectTab(t.key)}
              aria-current={active}
              className="shrink-0 px-2.5 py-1 text-[12.5px] whitespace-nowrap"
              style={{
                background: active ? 'var(--ws-panel-2)' : 'transparent',
                color: active ? 'var(--ws-tx)' : 'var(--ws-tx-2)',
                fontWeight: active ? 600 : 400,
                borderBottom: `2px solid ${active ? 'var(--ws-amber)' : 'transparent'}`,
              }}
            >
              {t.title}
              {t.count != null && (
                <span className="ws-num ml-1.5 text-[11px]" style={{ color: 'var(--ws-mut)' }}>
                  {t.count}
                </span>
              )}
            </button>
          )
        })}
      </nav>

      <button
        onClick={onAsk}
        className="shrink-0 px-3 py-1.5 text-[12.5px]"
        style={{
          background: 'var(--ws-panel-2)',
          color: 'var(--ws-tx)',
          border: '1px solid var(--ws-line)',
        }}
      >
        Ask
      </button>
    </div>
  )
}

/** The copilot, over the page, on a window too narrow to seat it beside one. */
function NarrowAsk({ onClose, ...rail }) {
  // Escape closes it. A full-screen panel with only a small button to leave is
  // a trap on the one screen size where the page is already short of room.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="lg:hidden fixed inset-0 z-40 flex flex-col" style={{ background: 'var(--ws-bg)' }}>
      <div
        className="shrink-0 flex justify-end px-3 py-2"
        style={{ borderBottom: '1px solid var(--ws-line-2)' }}
      >
        <button onClick={onClose} className="text-[12px]" style={{ color: 'var(--ws-mut)' }}>
          Close
        </button>
      </div>
      <div className="flex-1 min-h-0">
        <CopilotRail {...rail} />
      </div>
    </div>
  )
}

/**
 * What the page is reading, while it reads it.
 *
 * The lines come from the server, which builds them from the resolvers that are
 * actually about to run — so this can never claim the page consulted something
 * it did not open. A wait screen saying "calibrating to your workflow" would be
 * describing work nobody does: the same class of lie as a fabricated finding,
 * told earlier.
 */
function ReadingNotice({ reads, tab }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'var(--ws-bg)' }}>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5">
          <Loader2 className="animate-spin shrink-0" size={16} style={{ color: 'var(--ws-mut)' }} />
          <p className="text-[13px]" style={{ color: 'var(--ws-tx-2)' }}>
            {/* Naming the destination is what confirms the click landed. */}
            {/* The tab KEY is lowercase; the label the user clicked is not. */}
            {tab ? `Opening ${tab.charAt(0).toUpperCase()}${tab.slice(1)}` : 'Loading'}
          </p>
        </div>
        {/* list-none matters: without it the browser draws its own marker next
            to the one below, and every line reads "* ·". */}
        {reads?.length > 0 && (
          <ul className="mt-3 pl-[26px] list-none text-[12px] leading-relaxed" style={{ color: 'var(--ws-mut)' }}>
            {reads.map((r) => (
              <li key={r} className="flex gap-2">
                <span aria-hidden="true">·</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default function Workspace() {
  const [tab, setTab] = useState(DEFAULT_TAB)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [reads, setReads] = useState([])
  const navigate = useNavigate()

  // Drafting state, keyed by item so two rows cannot overwrite each other.
  const [drafts, setDrafts] = useState({})
  // Which item the rail is currently showing. The rail holds one at a time —
  // it is a workspace, not a log.
  const [active, setActive] = useState(null)
  // Only consulted below the three-column breakpoint, where the copilot has no
  // column to live in. See NarrowBar.
  const [asking, setAsking] = useState(false)
  const [activeTitle, setActiveTitle] = useState('')

  // Items the user has just dealt with. Removed from the page immediately —
  // waiting for a reload to confirm is how a button comes to feel unresponsive,
  // and this one is pressed precisely because the row should not be there.
  const [dismissed, setDismissed] = useState({})

  const onAct = useCallback(async (actionKey, item) => {
    if (!item) return

    if (actionKey === 'done' || actionKey === 'snooze' || actionKey === 'not_useful') {
      const state = actionKey === 'snooze' ? 'snoozed' : actionKey
      setDismissed((d) => ({ ...d, [item.id]: state }))
      try {
        const token = localStorage.getItem('amtrend_token')
        const res = await fetch('/api/workspace/item-state', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ item_id: item.id, state }),
        })
        if (!res.ok) throw new Error('not saved')
      } catch {
        // Put it back. An item that vanished without being recorded would
        // return on the next load, and the user would rightly stop trusting
        // the button.
        setDismissed((d) => {
          const next = { ...d }
          delete next[item.id]
          return next
        })
      }
      return
    }

    if (actionKey !== 'draft') return
    setActive(item.id)
    setActiveTitle(item.title || '')
    setDrafts((d) => ({ ...d, [item.id]: { status: 'working' } }))
    try {
      const token = localStorage.getItem('amtrend_token')
      const res = await fetch('/api/workspace/draft', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: item.id, tab }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setDrafts((d) => ({ ...d, [item.id]: { status: 'error', message: data.detail || `Failed (${res.status})` } }))
        return
      }
      setDrafts((d) => ({ ...d, [item.id]: { status: 'done', ...data } }))
    } catch (e) {
      setDrafts((d) => ({ ...d, [item.id]: { status: 'error', message: e.message } }))
    }
  }, [tab])

  // Which tab the user most recently asked for. A page load takes seconds, so
  // two things go wrong without this: a response for the tab they just left
  // arrives last and overwrites the one they are looking at, and a second click
  // on the same tab restarts work already in flight.
  const wanted = useRef(DEFAULT_TAB)

  const load = useCallback(async (key) => {
    wanted.current = key
    // Clear FIRST. The old tab's content used to sit there for the whole load,
    // so a click produced no visible change and people clicked again — which is
    // exactly what it looked like: a dead button.
    setData(null)
    setReads([])
    setError(null)
    setLoading(true)
    try {
      const token = localStorage.getItem('amtrend_token')

      // Ask what this will read BEFORE asking for it. The content request is the
      // thing being waited on, so it cannot also be what explains the wait.
      fetch(`/api/workspace/${key}/plan`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => { if (wanted.current === key) setReads(p?.reads || []) })
        .catch(() => { if (wanted.current === key) setReads([]) })

      const res = await fetch(`/api/workspace/${key}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      // The user moved on while this was in flight. Its answer is about a tab
      // they are no longer looking at, so it is dropped rather than rendered.
      if (wanted.current !== key) return

      if (res.status === 404) {
        // The Phase 0 development gate, and also the answer for a tab whose
        // connectors this tenant lacks. 404 rather than 403: something that
        // cannot exist here should not advertise itself.
        setError('The workspace is not available yet.')
        return
      }
      if (!res.ok) throw new Error(`Failed to load workspace (${res.status})`)
      const body = await res.json()
      if (wanted.current !== key) return
      setData(body)
      setError(null)
    } catch (e) {
      if (wanted.current === key) setError(e.message || 'Failed to load workspace')
    } finally {
      if (wanted.current === key) setLoading(false)
    }
  }, [])

  useEffect(() => { load(tab) }, [tab, load])

  // Shown on every load, including a tab switch — not only the first. The
  // switch is where the absence of it read as a broken button.
  if (loading && !data) return <ReadingNotice reads={reads} tab={tab} />

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center" style={{ background: 'var(--ws-bg)' }}>
        <p className="text-sm" style={{ color: 'var(--ws-tx-2)' }}>{error}</p>
        <button onClick={() => navigate('/')} className="text-sm underline" style={{ color: 'var(--ws-tx)' }}>
          Back to Trendy
        </button>
      </div>
    )
  }

  const keep = (i) => !dismissed[i.id]
  const grouped = data?.tab?.layout === 'grouped'
  const buckets = (data?.buckets || [])
    .map((b) => ({ ...b, items: (b.items || []).filter(keep),
                   groups: (b.groups || []).map((g) => ({ ...g, items: (g.items || []).filter(keep) }))
                             .filter((g) => g.items.length) }))
    .filter((b) => b.items.length)
  const items = (data?.items || []).filter(keep)
  const empty = grouped ? buckets.length === 0 : items.length === 0

  // One set of copilot props, rendered either in its column or over the page.
  // Built once so the two paths cannot drift into showing different things.
  const copilot = {
    context: data?.tab?.title || 'Today',
    draft: active ? drafts[active] : null,
    draftFor: activeTitle,
    onDismiss: () => setActive(null),
    askContext: activeTitle
      ? `the user is looking at "${activeTitle}" on the ${tab} tab`
      : `the user is looking at the ${tab} tab of their workspace`,
  }

  return (
    <div className="h-screen flex overflow-hidden" style={{ background: 'var(--ws-bg)' }}>
      <ResizableRail id="seat" side="left" defaultWidth={212} min={172} max={340}>
        <SeatRail
          seat={data?.seat}
          tabs={data?.tabs}
          activeTab={tab}
          onSelectTab={setTab}
          sources={data?.sources}
        />
      </ResizableRail>

      <main className="flex-1 min-w-0 px-7 py-5 pb-16 overflow-y-auto">
        <div
          className="flex justify-between items-baseline gap-4 flex-wrap pb-3"
          style={{ borderBottom: '1px solid var(--ws-line-2)' }}
        >
          <h1 className="text-xl font-semibold tracking-tight" style={{ color: 'var(--ws-tx)' }}>
            {data?.tab?.title}
          </h1>
          <p className="text-xs" style={{ color: 'var(--ws-mut)' }}>
            {new Date().toLocaleDateString(undefined, {
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
            })}
          </p>
        </div>

        {data?.tab?.lede && (
          <p className="my-4 text-xs max-w-[80ch]" style={{ color: 'var(--ws-mut)' }}>
            {data.tab.lede}
          </p>
        )}

        {data?.preview && <PreviewNotice />}
        {/* Missing sources come before content: a page running on half its
            sources must not read as a finished answer. */}
        <SetupGaps setup={data?.setup} />
        <Notes notes={data?.notes} />
        <ComposedSlot composed={data?.composed} />

        {grouped
          ? buckets.map((b) => <Bucket key={b.key} bucket={b} onAct={onAct} drafts={drafts} />)
          : (
            <div style={{ borderTop: '1px solid var(--ws-line-2)' }}>
              {items.map((item) => (
                <Row key={item.id} item={item} onAct={onAct} draft={drafts[item.id]}
                     tone={item.bucket === 'your_court' ? 'hot' : 'plain'} />
              ))}
            </div>
          )}

        {empty && (
          <p className="text-sm" style={{ color: 'var(--ws-mut)' }}>
            {/* An empty page means two different things and must never claim
                the wrong one. With a source disconnected the honest statement
                is "we could not look", not "there is nothing to do". */}
            {data?.setup?.length
              ? 'Nothing to show from the sources that are connected.'
              : 'Nothing to show. Sections appear only when they have something in them.'}
          </p>
        )}
      </main>

      <ResizableRail id="copilot" side="right" defaultWidth={330} min={260} max={520}>
        <CopilotRail {...copilot} />
      </ResizableRail>

      {/* Below lg neither rail is on screen. These put their contents back —
          the same components, reached a different way. */}
      <NarrowBar
        tabs={data?.tabs}
        activeTab={tab}
        onSelectTab={setTab}
        onAsk={() => setAsking(true)}
      />
      {asking && <NarrowAsk onClose={() => setAsking(false)} {...copilot} />}
    </div>
  )
}
